import type { SB } from '@play-co/replicant';
import type { MutableState } from '../State';
import type { ScheduledActionAPI } from '../utils/types';
import ab from '../ruleset/ab';
import { getAssignOnceFn } from '../ab/ab.modifiers';
import { sendAnalyticsEvents } from '../analytics';
import { getLocale } from '../getters/user';
import { isGuestUser } from '../guest/guest.getters';
import { getJSTDateTime } from '../utils/time';
import { getRandomItemsFromArr, getRandomItemFromArray } from '../utils/random';
import {
  getNextChatMessageSchedule,
  petSpeciesMessages,
} from '../petChatbot/petChatbot.ruleset';
import { preEntryInviteCreatives, preEntrySteps } from './preEntry.ruleset';
import { getPreEntryPayload } from './preEntry.getters';
import type { preEntryMessageSchema } from './preEntry.scheduledActions';

/** API shared by follow webhooks and first-entry cancellation. */
type PreEntryAPI = Pick<
  ScheduledActionAPI,
  | 'abTests'
  | 'chatbot'
  | 'date'
  | 'math'
  | 'scheduledActions'
  | 'sendAnalyticsEvents'
>;

/**
 * Enroll an OA follower once and schedule the six JST-calendar messages.
 * @param state Subscriber state, including users created by the webhook.
 * @param subscribedAt Original LINE follow timestamp; redelivery must not move D0.
 * @param api Replicant assignment, scheduling, clock, random, and analytics APIs.
 */
export function enrollPreEntryMessages(
  state: MutableState,
  subscribedAt: number,
  api: PreEntryAPI,
): void {
  // Prior players, guests, and followers who already blocked the OA are ineligible.
  if (
    state.loginCount !== 0 ||
    isGuestUser(state) ||
    !api.chatbot.getLastKnownIsSubscribed()
  )
    return;

  // This manually assigned test also identifies earlier enrollment; never restart it.
  const testId = ab.tests.TEST_PRE_ENTRY_CHATBOT;
  if (api.abTests.getBucketID(testId)) return;
  getAssignOnceFn(state, api)(testId);
  const bucketId = api.abTests.getBucketID(testId);

  // Inactive, stopped, or unrolled tests do not produce a bucket or treatment.
  if (!bucketId) return;

  // Treatment occurs outside the client; identify both buckets immediately, per Replicant docs.
  // Use Replicant directly: $identify is a reserved operation, not a schema event.
  api.sendAnalyticsEvents([
    {
      eventType: '$identify',
      eventProperties: {},
      userProperties: { abTest_0498_pre_entry_chatbot: bucketId },
    },
  ]);
  if (bucketId !== 'enabled') return;

  // Fix the two distinct invite choices once, before any delivery or retry.
  const invites = getRandomItemsFromArr(
    [...preEntryInviteCreatives],
    2,
    api.math.random,
  );
  const d0 = getJSTDateTime(subscribedAt).startOf('day');
  const messages: SB.ExtractType<typeof preEntryMessageSchema>[] = [
    {
      step: 'd1_morning',
      sendAt: d0.plus({ days: 1, hours: 9 }).toMillis(),
      content: { kind: 'invite', creative: invites[0] },
    },
    {
      step: 'd1_evening',
      sendAt: d0.plus({ days: 1, hours: 18 }).toMillis(),
      content: { kind: 'invite', creative: invites[1] },
    },
  ];

  // Use the original greeting clock and its 0–60 minute variation, only on D2.
  let greetingCursor = d0.plus({ days: 1, hours: 20 }).toMillis();
  for (const step of ['d2_morning', 'd2_lunch', 'd2_evening'] as const) {
    const greeting = getNextChatMessageSchedule(
      greetingCursor,
      api.math.random(),
    );
    greetingCursor += greeting.delayInMS;
    const message = getRandomItemFromArray(
      petSpeciesMessages.ball.filter(({ time }) => time === greeting.period),
      api.math.random,
    );
    messages.push({
      step,
      sendAt: greetingCursor,
      content: { kind: 'greeting', messageId: message.id },
    });
  }
  messages.push({
    step: 'd3_reengagement',
    sendAt: d0.plus({ days: 3, hours: 9 }).toMillis(),
    content: { kind: 'reengagement' },
  });

  // Do not catch up past slots after a delayed webhook; retain the original calendar.
  const now = api.date.now();
  for (const message of messages) {
    if (message.sendAt < now) continue;

    // Each step has its own cancellable job and creative-specific schedule analytics.
    api.scheduledActions.schedule.sendPreEntryMessage({
      args: message,
      notificationId: `preEntry_${message.step}`,
      delayInMS: message.sendAt - now,
    });
    const payload = getPreEntryPayload(
      message.content,
      message.step,
      getLocale(state),
    );
    sendAnalyticsEvents(api, [
      {
        eventType: 'PreEntryMessageScheduled',
        eventProperties: {
          feature: payload.feature,
          $subFeature: payload.$subFeature,
          $creativeAssetID: payload.$creativeAssetID,
          sendAt: message.sendAt,
        },
      },
    ]);
  }
}

/**
 * Cancel only pre-entry schedules; delivery separately checks login and subscription.
 * Already-dequeued retries cannot be revoked by this API. A block followed by an
 * unblock before first entry can make an old retry eligible again (deferred).
 * @param api Replicant scheduler.
 */
export function cancelPreEntryMessages(
  api: Pick<PreEntryAPI, 'scheduledActions'>,
): void {
  // Stable notification IDs allow cancellation without tracking jobs in player state.
  for (const step of preEntrySteps) {
    api.scheduledActions.unschedule(`preEntry_${step}`);
  }
}
